<script setup lang="ts">
import type { Temporal } from 'temporal-polyfill';
import type { ComponentPublicInstance } from 'vue';
import type { CalendarNavigation, CalendarViewMode } from '#layers/listing/constants/calendar';
import type { CalendarDisplayOptions } from '#layers/listing/types/layout';
import type { CalendarRange } from './build-calendar-range-filter';
import { unrefElement } from '@vueuse/core';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { calendarDateConverter, useUserLocale } from '#layers/base/public';
import { CALENDAR_VIEW_DEFINITIONS, CalendarNavigations, CalendarViewModes } from '#layers/listing/constants/calendar';
import { LISTING_LOADING_DELAY_MS } from '#layers/listing/constants/loading';
import { calendarSingleUi, pickerPopoverUi, useDeferredTrue } from '#layers/ui/public';
import { formatCalendarTitle } from './format-calendar-title';

const {
	range,
	focusedDate,
	firstDayOfWeek,
	loading,
} = defineProps<{
	range: CalendarRange;
	focusedDate: Temporal.PlainDate;
	firstDayOfWeek: CalendarDisplayOptions['firstDayOfWeek'];
	loading: boolean;
}>();

const emit = defineEmits<{
	navigate: [navigation: CalendarNavigation];
	pickDate: [date: Temporal.PlainDate];
}>();

const { t } = useI18n();
const { formatLocale } = useUserLocale();

const currentView = defineModel<CalendarViewMode>({ required: true });

const viewOptions = Object.values(CalendarViewModes).map((mode) => ({ label: t(`layout.calendar.views.${mode}`), value: mode }));

const stepUnit = computed(() => t(`layout.calendar.units.${CALENDAR_VIEW_DEFINITIONS[currentView.value].step}`));

const prevLabel = computed(() => t('layout.calendar.nav.previous', { unit: stepUnit.value }));
const nextLabel = computed(() => t('layout.calendar.nav.next', { unit: stepUnit.value }));

const headerTitle = computed(() => formatCalendarTitle(currentView.value, range, formatLocale.value));

const showLoading = useDeferredTrue(() => loading, LISTING_LOADING_DELAY_MS);

const isPickerOpen = ref(false);

const pickerDate = computed({
	get: () => calendarDateConverter.toCalendar(focusedDate),
	set: (value) => {
		const date = calendarDateConverter.toTemporal(value);
		if (!date) return;

		isPickerOpen.value = false;
		emit('pickDate', date);
	},
});

const titleButton = useTemplateRef<ComponentPublicInstance>('titleButton');

// Moves focus to the title, because a drill-down removes the element that had focus.
function focusTitle() {
	unrefElement(titleButton)?.focus();
}

defineExpose({ focusTitle });
</script>

<template>
	<div class="@container flex shrink-0 items-center gap-2 px-6 py-2">
		<!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -- the picker button inside renders the visible title -->
		<h2 class="flex min-w-0">
			<DPopover
				v-model:open="isPickerOpen"
				:content="{ side: 'bottom', align: 'start' }"
				:ui="pickerPopoverUi"
			>
				<DButton
					ref="titleButton"
					color="neutral"
					variant="ghost"
					trailing-icon="lucide:chevron-down"
					:label="headerTitle"
					class="-ms-2 h-7 min-w-0 text-lg"
				/>

				<template #content>
					<DCalendar
						v-model="pickerDate"
						:week-starts-on="firstDayOfWeek"
						:ui="calendarSingleUi"
						prevent-deselect
					/>
				</template>
			</DPopover>
		</h2>

		<!-- Stays mounted, so screen readers announce the loading text when it appears. -->
		<div
			role="status"
			class="flex size-4 shrink-0 items-center"
		>
			<template v-if="showLoading">
				<DSpinner class="size-4 text-muted" />
				<span class="sr-only">{{ $t('common.loading') }}</span>
			</template>
		</div>

		<div class="ms-auto flex shrink-0 items-center gap-1">
			<DButton
				icon="lucide:chevron-left"
				color="neutral"
				variant="ghost"
				square
				size="sm"
				:aria-label="prevLabel"
				:title="prevLabel"
				@click="$emit('navigate', CalendarNavigations.Previous)"
			/>
			<DButton
				icon="lucide:chevron-right"
				color="neutral"
				variant="ghost"
				square
				size="sm"
				:aria-label="nextLabel"
				:title="nextLabel"
				@click="$emit('navigate', CalendarNavigations.Next)"
			/>
			<DButton
				color="neutral"
				variant="outline"
				size="sm"
				class="ms-1 hidden @w-sm:flex"
				@click="$emit('navigate', CalendarNavigations.Today)"
			>
				{{ $t('layout.calendar.today') }}
			</DButton>
			<DSelect
				v-model="currentView"
				:items="viewOptions"
				size="sm"
				:ui="{ base: 'py-1.5 ring-accented' }"
				class="ms-1 w-28"
			/>
		</div>
	</div>
</template>
